import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, take, tap } from 'rxjs/operators';
import { PricingPlan } from 'src/app/components/billing/billing-types';
import { PlanCardClickEvent } from 'src/app/components/new-billing/plan-card/plan-card.component';
import { ConcretePlan } from 'src/app/pages/new-billing/models/plan.model';
import { SubscriptionModel } from 'src/app/pages/new-billing/models/subscription.model';
import { UpgradeDowngradeBaseModel } from 'src/app/pages/new-billing/models/upgrade-downgrade-model/upgrade-downgrade-base.model';
import { LegacyPricingPlans, PlanActionType, PricePeriodicity } from 'src/app/pages/new-billing/types/plan.types';
import { UpgradeBillingResult } from 'src/app/pages/new-billing/upgrade-billing-details/upgrade-billing-details.component';
import { planIdToPricingPlan } from 'src/app/pages/new-billing/utils/plan-maps';
import { UpgradeConfirmModalTrackingContext } from 'src/app/pages/new-subscribe-plans/store/model';
import { BillingActionsService } from 'src/app/services/billing/billing-actions.service';
import { LocalizedPricingService } from 'src/app/services/localized-pricing/localized-pricing.service';
import { convertCentsToDollars, normalizePrice } from 'src/app/util/price-formatter.util';

type CardPlanStates = {
  [key in LegacyPricingPlans<'annual' | 'monthly'>]: {
    downgrade: PricingPlan[];
    upgrade: PricingPlan[];
  }
};

export interface BadgeInfo {
  className: string;
  translationKey: string;
}

export type BadgeType = 'popular' | 'current' | null;

@Injectable({
  providedIn: 'root',
})
export class PlanStateService {

  private readonly planStates: CardPlanStates = {
    basic_plan: {
      downgrade: [],
      upgrade: ['basic_plan_annual', 'standard_plan', 'standard_plan_annual', 'premium_plan', 'premium_plan_annual'],
    },
    basic_plan_annual: {
      downgrade: ['basic_plan'],
      upgrade: ['standard_plan', 'standard_plan_annual', 'premium_plan', 'premium_plan_annual'],
    },
    standard_plan: {
      downgrade: ['basic_plan', 'basic_plan_annual'],
      upgrade: ['standard_plan_annual', 'premium_plan', 'premium_plan_annual'],
    },
    standard_plan_annual: {
      downgrade: ['basic_plan', 'basic_plan_annual', 'standard_plan'],
      upgrade: ['premium_plan', 'premium_plan_annual'],
    },
    premium_plan: {
      downgrade: ['basic_plan', 'basic_plan_annual', 'standard_plan', 'standard_plan_annual'],
      upgrade: ['premium_plan_annual'],
    },
    premium_plan_annual: {
      downgrade: ['basic_plan', 'basic_plan_annual', 'standard_plan', 'standard_plan_annual', 'premium_plan'],
      upgrade: [],
    },
  };

  constructor(
    private readonly translate: TranslateService,
    private readonly billingActions: BillingActionsService,
    private readonly router: Router,
    private readonly localizedPricingService: LocalizedPricingService,
  ) { }

  determineCardPlanName(planName: LegacyPricingPlans, payRecurrence: PricePeriodicity): LegacyPricingPlans {
    const planSuffix = payRecurrence === 'year' ? '_annual' : '';
    return `${planName}${planSuffix}` as LegacyPricingPlans;
  }

  determinePlanState(
    plan: ConcretePlan,
    currentPlan: ConcretePlan,
    payRecurrence: PricePeriodicity,
  ): PlanActionType {
    if (!plan || !currentPlan) return null;

    const cardPlanName = this.determineCardPlanName(plan.name, payRecurrence);

    if (this.isFutureSubscription(plan, currentPlan, cardPlanName)) return 'future';

    if (cardPlanName === currentPlan.name) return 'current';

    // Validate that currentPlan.name exists in planStates before accessing its properties
    const currentPlanStates = this.planStates[currentPlan.name];
    if (!currentPlanStates) {
      return null;
    }

    if (currentPlanStates.downgrade.includes(cardPlanName)) return 'downgrade';
    if (currentPlanStates.upgrade.includes(cardPlanName)) return 'upgrade';

    // Explicitly return null when no plan state conditions are met
    return null;
  }

  isCurrentPlan(plan: ConcretePlan, currentPlan: ConcretePlan, payRecurrence: PricePeriodicity): boolean {
    return this.determinePlanState(plan, currentPlan, payRecurrence) === 'current';
  }

  isFutureSubscription(plan: ConcretePlan, currentPlan: ConcretePlan, cardPlanName?: LegacyPricingPlans): boolean {
    if (!currentPlan?.futurePlanId) {
      return false;
    }

    // Use the provided cardPlanName, or derive it from the current plan's payment recurrence
    const planName = cardPlanName || this.determineCardPlanName(plan.name, currentPlan.paymentModel?.paymentRecurrence || 'month');
    const futurePlanName = planIdToPricingPlan[currentPlan.futurePlanId];
    const hasDifferentFuturePlan = currentPlan.planId !== currentPlan.futurePlanId;

    return futurePlanName === planName && hasDifferentFuturePlan;
  }

  formatNextPaymentDate(isoString: string): string {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  }

  /**
   * Formats an ISO date to a long month with ordinal day, e.g., "September 29th".
   * Uses en-US locale and UTC timezone to match billing API expectations.
   */
  formatNextPaymentDateLong(isoString: string): string {
    if (!isoString) return '';
    const date = new Date(isoString);
    // Get day in UTC
    const day = Number(date.toLocaleDateString('en-US', { day: 'numeric', timeZone: 'UTC' }));
    const monthName = date.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });

    const suffix = (d: number) => {
      const v = d % 100;
      if (v >= 11 && v <= 13) return 'th';
      switch (d % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };

    if (!day || !monthName) return '';
    return `${monthName} ${day}${suffix(day)}`;
  }

  /**
   * Yearly plans are billed as 10 months (2 months free). The +0.5 matches the
   * rounding used on Choose Your Plan so both surfaces show the same monthly equivalent.
   */
  getYearlyEquivalentMonthlyPrice(monthlyPrice: number): number {
    return (monthlyPrice * 10 + 0.5) / 12;
  }

  calculatePriceToDisplay(plan: ConcretePlan, payRecurrence: PricePeriodicity): string {
    if (payRecurrence === 'year') {
      return this.getYearlyEquivalentMonthlyPrice(plan.pricePerRecurrence).toFixed(1);
    }
    return plan.pricePerRecurrence.toString();
  }

  /**
   * Gets the localized currency symbol for a plan
   * @param plan - The plan to get currency for
   * @returns string - The currency symbol
   */
  getLocalizedCurrencySymbol(plan: ConcretePlan): string {
    const billingCurrency = plan?.billingApiData?.billingDetails?.currency;
    const isEnabled = this.localizedPricingService.isLocalizedPricingEnabled();

    // Priority 1: Always respect billing currency if available
    if (isEnabled && billingCurrency) {
      return this.localizedPricingService.getCurrencySymbol(billingCurrency);
    }

    // Priority 2: Use geo-based currency only if localized pricing is enabled
    if (isEnabled) {
      const currency = this.localizedPricingService.getCurrencyFromPlan(plan);
      return this.localizedPricingService.getCurrencySymbol(currency);
    }

    // Priority 3: Fallback to USD
    return '$';
  }

  /**
   * Gets the localized price for a plan
   * @param plan - The plan to get price for
   * @param payRecurrence - The payment recurrence
   * @returns string - The localized price
   */
  getLocalizedPrice(plan: ConcretePlan, payRecurrence: PricePeriodicity): string {
    const billingCurrency = plan?.billingApiData?.billingDetails?.currency;

    // Priority 1: Always use billing currency if available and has matching price override
    if (billingCurrency && plan?.priceOverrides?.length) {
      const matchingPriceOverride = plan.priceOverrides.find(
        override => override.currencyCode === billingCurrency,
      );

      if (matchingPriceOverride) {
        const baseLocalizedPrice = convertCentsToDollars(parseFloat(matchingPriceOverride.unitPrice));
        const numericBasePrice = normalizePrice(baseLocalizedPrice);

        const adjustedPrice = payRecurrence === 'year'
          ? this.getYearlyEquivalentMonthlyPrice(numericBasePrice)
          : numericBasePrice;

        return this.localizedPricingService.formatPriceWithCurrency(adjustedPrice, billingCurrency);
      }
    }

    // Priority 2: If no card is added, try to get price from price overrides based on user's country
    const noCardPrice = this.localizedPricingService.getLocalizedPriceForNoCard(plan, plan.pricePerRecurrence);
    if (noCardPrice) {
      const adjustedPrice = payRecurrence === 'year'
        ? this.getYearlyEquivalentMonthlyPrice(noCardPrice.price)
        : noCardPrice.price;

      return this.localizedPricingService.formatPriceWithCurrency(adjustedPrice, noCardPrice.currency);
    }

    // Priority 3: Use LocalizedPricingService for consistent pricing logic
    // Calculate the base price using the standard formula
    const basePrice = this.calculatePriceToDisplay(plan, payRecurrence);
    const price = normalizePrice(parseFloat(basePrice));

    // Delegate to LocalizedPricingService for consistent formatting and geo-based pricing
    return this.localizedPricingService.formatLocalizedPriceForComponent(price, plan);
  }

  getRecurrenceMessage(payRecurrence: PricePeriodicity): string {
    return payRecurrence === 'year' ? 'billing.billedAnnual' : 'billing.billedMonthly';
  }

  getPlanDescription(planName: string): string {
    const basePlanName = planName.split('_')[0];
    return this.translate.instant('billing.' + basePlanName + 'PlanDescription');
  }

  getFeaturesListTitle(planName: string): string {
    const basePlanName = planName.split('_')[0];
    return this.translate.instant('billing.' + basePlanName + 'FeatureListTitle');
  }

  getRecurrenceMessageEachPlan(plan: ConcretePlan): string {
    const allPlanTitle = this.translate.instant('billing.AllPlans') + ' ';
    const prevPlan = this.translate.instant('billing.' + plan.tierUnderPlan + 'Plan') + ' plus:';
    return plan.hasFeaturesInOtherPlan() ? allPlanTitle + prevPlan : 'billing.basicPlanIncludes';
  }

  calculateBadgeInfo(
    showPopularBadge: boolean,
    isCurrentPlan: boolean,
    isV2: boolean = false,
    useRecommendedLabel: boolean = false,
  ): BadgeInfo {
    const type = this.getBadgeType(showPopularBadge, isCurrentPlan);

    if (this.shouldHideBadge(type, isV2)) {
      return { className: '', translationKey: '' };
    }

    const badgeConfig: Record<BadgeType, BadgeInfo> = {
      popular: {
        className: 'popular-badge',
        translationKey: useRecommendedLabel ? 'billing.recommended' : 'billing.popular',
      },
      current: { className: 'current-plan-badge', translationKey: 'billing.currentPlan' },
    };

    return badgeConfig[type];
  }

  getTrackingData(action: 'upgrade' | 'downgrade', subscription?: SubscriptionModel) {
    const actionText = action === 'upgrade' ? 'Upgrade' : 'Downgrade';
    return {
      page: `Clicked ${actionText} Plan on Billing Details`,
      trialstartdate: subscription?.trialStartDate,
      trialenddate: subscription?.trialEndDate,
    };
  }

  createPlanStateGetter(
    plan: ConcretePlan,
    currentPlan: ConcretePlan,
    payRecurrence: PricePeriodicity,
  ): () => PlanActionType {
    return () => this.determinePlanState(plan, currentPlan, payRecurrence);
  }

  private getBadgeType(showPopularBadge: boolean, isCurrentPlan: boolean): BadgeType {
    if (showPopularBadge) {
      return 'popular';
    }
    if (isCurrentPlan) {
      return 'current';
    }
    return null;
  }

  private shouldHideBadge(type: BadgeType, isV2: boolean): boolean {
    return !type || (isV2 && type !== 'popular');
  }

  handlePlanChange(
    { plan, actionType }: PlanCardClickEvent,
    upgradeDowngradeModel: UpgradeDowngradeBaseModel,
    onPlanChanged?: () => void,
    isV2 = false,
    returnUrl?: string,
    successSnackbarMessage?: string,
    trackingContext?: UpgradeConfirmModalTrackingContext,
    previousPlanId?: string,
  ): void {
    const dialogRef = upgradeDowngradeModel.openDialog(plan, {
      actionType,
      trackingContext,
      previousPlanId,
    }, isV2);
    this.setDialogAfterClosed(dialogRef, onPlanChanged, returnUrl, successSnackbarMessage);
  }

  private setDialogAfterClosed(
    dialogRef: MatDialogRef<unknown, UpgradeBillingResult>,
    onPlanChanged?: () => void,
    returnUrl?: string,
    successSnackbarMessage?: string,
  ): void {
    dialogRef.afterClosed()
      .pipe(
        take(1),
        filter((data) => data?.didChangePlan),
        tap(() => onPlanChanged?.()),
      )
      .subscribe(() => {
        this.billingActions.openSnackbar({
          message: successSnackbarMessage ?? this.translate.instant('billing.planChanged'),
        });
        if (returnUrl) {
          void this.router.navigateByUrl(returnUrl);
        } else {
          void this.router.navigate(['/new-billing'], { queryParams: { choosePlan: 'no' } });
        }
      });
  }
}
